import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import NavDropdown from 'components/navV2/NavDropdown';
import NavLink from 'components/navV2/NavLink';
import NavLinkColumn from 'components/navV2/NavLinkColumn';
import NavPrimaryRegistryDropdown from 'components/navV2/NavPrimaryRegistryDropdown';
import NavSecondaryRegistryDropdown from 'components/navV2/NavSecondaryRegistryDropdown';

import hasChildren from 'util/hasChildren';
import rmvSpaceAndSpecial from 'util/rmvSpaceAndSpecial';
import { trackNavigationClicked } from 'util/trackingHelper';
import {
  buildNavItemEventData,
  buildNavSubMenuEventData,
} from 'util/trackNavigationClickedHelpers';
import TextUtil from 'util/TextUtil';

const getMenuPosition = (node = {}, isPrimary) => {
  if (node.title === 'Kitchen') return 'fullWidth';
  if (node.dockRight) return 'right';
  if (node.dockLeft) return 'left';
  if (node.centerDropdown || isPrimary) return 'center';
  return 'left';
};

/**
 * This is used to draw nav menu with drop downs.  It is used to draw the primary nav on
 * the home store, and the secondary nav elsewhere.  It does not draw the priamry navigation
 * menu on the web (non home store)
 *
 * @param {*} param0
 * @returns
 */
const NavMapper = ({ navData, type, className, activeLinkId }) => {
  const classNamePrefix = `nav${type}`;
  const isPrimary = type === 'Primary';

  if (!navData.length) return null;

  // Renders the primary and secondary nav sub-menus;
  const renderChildren = (node = {}, grandparent) => {
    if (!hasChildren(node)) return null;

    // Registry drop menu is a special bb
    if (node.id === 'registry') return <NavPrimaryRegistryDropdown node={node} />;

    const onClick = (child, index) => {
      const isMultiColumnDropdown = Boolean(grandparent);
      const eventData = buildNavSubMenuEventData({
        grandparent,
        parent: node,
        child,
        type,
        // MultiColumnDropdowns have a header in the first dropdown row, so bump the index 1.
        index: isMultiColumnDropdown ? index + 1 : index,
      });
      trackNavigationClicked(eventData);
    };

    return (
      <NavLinkColumn
        links={node.children.map((child, i) => {
          return (
            <NavLink
              key={i}
              href={child.path}
              variant={`${type}NavSubLink`}
              newTag={child.newTag}
              showJewel={child.showJewel}
              icon={child.icon}
              onClick={() => onClick(child, i)}
            >
              {child.title}
            </NavLink>
          );
        })}
      />
    );
  };

  // Sub-menus with multple columns
  const renderSubHeaderWithChildren = (firstNode = {}) => {
    if (!hasChildren(firstNode)) return null;

    const onClick = (link, i) => {
      const eventData = {
        menu_item: TextUtil.toAllUpperCase(link.title),
        section: TextUtil.toAllUpperCase(`${type}_SUB_MENU`),
        node_level_1: TextUtil.toAllUpperCase(firstNode.parent),
        node_level_2: type === 'Primary' ? undefined : TextUtil.toAllUpperCase(firstNode.title),
        row_position: 1,
        column_position: i + 1,
      };
      trackNavigationClicked(eventData);
    };

    return firstNode.children.map((secondNode, i) => (
      <div className={`${classNamePrefix}__dropdown-section`} key={i}>
        <NavLink
          href={secondNode.path}
          variant={`${type}NavSubHeader`}
          onClick={() => onClick(secondNode, i)}
        >
          {secondNode.title}
        </NavLink>
        {renderChildren(secondNode, firstNode)}
      </div>
    ));
  };

  const renderDropdown = (node = {}) => {
    const nodeHasGrandchildren = node.children.some((child) => hasChildren(child));
    const classes = cx(
      `${classNamePrefix}__dropdown`,
      [`${classNamePrefix}__dropdown--${rmvSpaceAndSpecial(node.title)}`],
      { [`${classNamePrefix}__dropdown--hasGrandchildren`]: nodeHasGrandchildren }
    );

    const renderWhichNode = () => {
      // Registry drop menu is a special bb
      if (node.id === 'your-registry') return <NavSecondaryRegistryDropdown node={node} />;
      if (nodeHasGrandchildren) return renderSubHeaderWithChildren(node); // Multiple columns
      return renderChildren(node);
    };

    const eventData = buildNavItemEventData({ node, type });

    return (
      <NavDropdown
        className={classes}
        label={node.title}
        link={{ href: node.path }}
        eventType="hover"
        variant={type.toLowerCase()}
        showCaret={node.showCaret || isPrimary}
        menuPositionX={getMenuPosition(node, isPrimary)}
        isActive={activeLinkId === node.id}
        onClick={() => trackNavigationClicked(eventData)}
        icon={node.icon}
      >
        {renderWhichNode()}
      </NavDropdown>
    );
  };

  const renderLink = (node = {}) => {
    const eventData = buildNavItemEventData({ node, type });

    return (
      <NavLink
        href={node.path}
        variant={`${type}NavLink`}
        newTag={node.newTag}
        icon={node.icon}
        showJewel={node.showJewel}
        isActive={activeLinkId === node.id}
        className={`nav${type}__${node.id}`}
        onClick={() => trackNavigationClicked(eventData)}
      >
        {node.title}
      </NavLink>
    );
  };

  if (!navData.length) return null;
  // Renders the Primary or Secondary Nav items.
  return (
    <div className={cx(`${classNamePrefix}__container`, className)}>
      <ul className={`${classNamePrefix}__links`}>
        {navData.map((node, i) => {
          const nodeHasChildren = hasChildren(node);
          const classes = cx(`${classNamePrefix}__link`, {
            [`${classNamePrefix}__link--hasChildren`]: nodeHasChildren,
          });

          return (
            <li key={i} className={classes}>
              {nodeHasChildren ? renderDropdown(node) : renderLink(node)}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

NavMapper.propTypes = {
  type: PropTypes.oneOf(['Primary', 'Secondary']).isRequired,
  navData: PropTypes.arrayOf(PropTypes.object),
  className: PropTypes.string,
  activeLinkId: PropTypes.string,
};

NavMapper.defaultProps = {
  navData: [],
  activeLinkId: '',
};

export default NavMapper;
